import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Form as FinalForm, Field } from 'react-final-form';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

// Components
import Form from '../../Form';
import InputField from '../../InputField';
import PasswordField from '../../PasswordField';
import Subheading from '../../Subheading';
import Actions from '../../Actions';
import Button from '../../Button';
import FieldError from '../../FieldError';
import SuccessMessage from '../../SuccessMessage';

// State
import { recoveryPassword, recoveryPasswordReset } from '../../../state/modules/user/actions';

// Utils
import {
  composeValidators,
  required,
  passwordIsEqual,
  strongPassword,
} from '../../../utils/validators';

// Styles
import styles from './RecoveryPasswordSecondStepForm.styl';

const cx = classNames.bind(styles);

export default function RecoveryPasswordSecondStepForm(props) {
  const { className, goToSignIn, onCancel } = props;

  const dispatch = useDispatch();
  const { isRecovered, phone, email, error, isRecovering } = useSelector(
    state => state.user.recoveryPassword,
  );

  const submit = values => {
    return phone
      ? dispatch(recoveryPassword({ phone, ...values }))
      : dispatch(recoveryPassword({ email, ...values }));
  };

  useEffect(() => {
    return () => {
      if (isRecovered) {
        dispatch(recoveryPasswordReset());
      }
    };
  }, [isRecovered]);

  if (isRecovered) {
    return (
      <div className={cx('RecoveryPasswordSecondStepForm', className)}>
        <FormattedMessage id="recoveryPassword.successMessage.text">
          {msg => (
            <SuccessMessage text={msg}>
              <Button onClick={goToSignIn}>
                <FormattedMessage id="button.signIn" />
              </Button>
            </SuccessMessage>
          )}
        </FormattedMessage>
      </div>
    );
  }

  return (
    <div className={cx('RecoveryPasswordSecondStepForm', className)}>
      <Subheading className={cx('RecoveryPasswordSecondStepForm__note')}>
        {phone ? (
          <FormattedMessage id="recoveryPassword.phone" values={{ phone }} />
        ) : (
          <FormattedMessage id="recoveryPassword.email" values={{ email }} />
        )}
      </Subheading>
      <FinalForm
        onSubmit={submit}
        render={({ handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <Field name="password" validate={composeValidators(required, strongPassword)}>
              {({ input }) => (
                <Form.Field>
                  <FormattedMessage id="field.newPassword.label">
                    {label => <PasswordField {...input} label={label} />}
                  </FormattedMessage>
                  <FieldError name="password" />
                </Form.Field>
              )}
            </Field>
            <Field
              name="password_repeat"
              validate={composeValidators(required, passwordIsEqual('password'))}
            >
              {({ meta, input }) => (
                <Form.Field>
                  <FormattedMessage id="field.repeatPassword.label">
                    {label => <PasswordField {...input} label={label} />}
                  </FormattedMessage>
                  <FieldError name="password_repeat" />
                  <Form.Error {...meta} error={error} />
                </Form.Field>
              )}
            </Field>
            <Field name="code" validate={required}>
              {({ input }) => (
                <Form.Field>
                  <InputField
                    {...input}
                    label={
                      phone ? (
                        <FormattedMessage id="field.smsCode.label">
                          {label => label}
                        </FormattedMessage>
                      ) : (
                        <FormattedMessage id="field.code.label">{label => label}</FormattedMessage>
                      )
                    }
                  />
                  <FieldError name="code" />
                </Form.Field>
              )}
            </Field>
            <Actions>
              <Button onClick={onCancel} link>
                <FormattedMessage id="button.cancel" />
              </Button>
              <Button type="submit" isLoading={isRecovering}>
                <FormattedMessage id="button.recoveryPassword" />
              </Button>
            </Actions>
          </Form>
        )}
      />
    </div>
  );
}

RecoveryPasswordSecondStepForm.defaultProps = {
  className: '',
};

RecoveryPasswordSecondStepForm.propTypes = {
  className: PropTypes.string,
  goToSignIn: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
